"use client";

import {
	Button,
	RadixSelect,
	RadixSelectItem,
	cn,
	getConstants,
	getDomainDisplayNames,
	getLocaleAsset,
	openInCurrentTab,
	openInNewTab,
	separateLocale,
} from "@tosize/ui";
import { type ReactNode, useState } from "react";
import Image from "~/components/utilities/Image";
import { getDomains, getHrefLangs } from "~/lib/utils";
import type { LangParams } from "../layout";

interface Props extends LangParams {
	title: string;
	className?: string;
	variant?: "country" | "language";
}

export function DomainSelector({ title, lang, className, variant = "country" }: Props) {
	const [value, setValue] = useState(lang);
	const { splashUrl } = getConstants();
	const { domains } = getDomains(lang);

	if (!domains?.length) {
		return null;
	}

	return (
		<RadixSelect
			title={title}
			value={value}
			onValueChange={(value) => {
				setValue(value);

				const item = domains.find((item) => item.locale === value);
				const hrefLang = getHrefLangs(value);

				return openInNewTab(hrefLang?.href ?? item?.href ?? splashUrl);
			}}
			onOpenChange={() => setValue(lang)}
			className={cn(className, "min-w-[10rem] bg-white")}
		>
			{domains.map((item, index) => {
				const { country } = separateLocale(item.locale);
				const { region, language } = getDomainDisplayNames(item.locale);

				return (
					<RadixSelectItem key={index} value={item.locale}>
						<div className="flex items-center gap-2">
							{variant === "language" ? (
								<span>{language}</span>
							) : (
								<>
									<Image
										src={getLocaleAsset(`countries/jpg/${country}.jpg`)}
										height="12"
										width="16"
										alt={region ?? language ?? country}
									/>
									<span>
										{region} ({language})
									</span>
								</>
							)}
						</div>
					</RadixSelectItem>
				);
			})}
		</RadixSelect>
	);
}

export function LanguageLink(props: {
	locale: string;
	href?: string;
	className?: string;
	children: ReactNode;
}) {
	return (
		<Button
			variant="link"
			className={props.className}
			onClick={(e) => {
				const hrefLang = getHrefLangs(props.locale);
				const href = hrefLang?.href ?? props.href;

				return href ? (e.metaKey ? openInNewTab(href) : openInCurrentTab(href)) : null;
			}}
		>
			{props.children}
		</Button>
	);
}
